import React, { useState } from 'react';
import {
  CheckCircle2, XCircle, Loader2, AlertCircle, X,
} from 'lucide-react';
import { marcarCita, guardarProgresoCita } from '../api';

// Mismo tope que el servidor (cierreCitaService.MOTIVO_MAX).
const MOTIVO_MAX = 2000;

/**
 * MarcarCitaModal — UI para cerrar una cita.
 *
 * El técnico elige estado (Realizada / No asistió), marca las subtareas que hizo y escribe notas
 * (opcional).
 *
 * Desde el 3/11:
 *   · «Guardar progreso» guarda las marcas SIN cerrar: la cita sigue abierta y muestra N/M
 *     (PUT /citas/:id/subtareas).
 *   · «Marcar como realizada» sólo con TODAS marcadas; si falta alguna, con un motivo obligatorio.
 *     La regla la aplica el servidor (422 SUBTAREAS_INCOMPLETAS); aquí sólo se anticipa.
 *
 * Backend: POST /citas/:id/marcar
 *   body: { estado, notas?, subtareas_completadas?: number[], motivo? }
 */
export default function MarcarCitaModal({ cita, onClose, onOk }) {
  const [estado, setEstado] = useState('realizada');
  const [notas, setNotas] = useState(cita.notas || '');
  const [motivo, setMotivo] = useState('');
  const [completadas, setCompletadas] = useState(
    () => new Set(cita.subtareas?.filter((s) => s.completada).map((s) => s.id) || [])
  );
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  function toggleSubtarea(id) {
    setCompletadas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function confirmar() {
    setGuardando(true);
    setError(null);
    try {
      await marcarCita(cita.id, {
        estado,
        notas: notas.trim() || null,
        subtareas_completadas: Array.from(completadas),
        ...(faltanSubtareas ? { motivo: motivo.trim() } : {}),
      });
      if (onOk) await onOk();
    } catch (e) {
      console.error('[marcarCita]', e);
      setError(e?.response?.data?.error || 'No se pudo marcar la cita.');
    } finally {
      setGuardando(false);
    }
  }

  async function guardarProgreso() {
    setGuardando(true);
    setError(null);
    try {
      await guardarProgresoCita(cita.id, Array.from(completadas));
      if (onOk) await onOk();
    } catch (e) {
      console.error('[guardarProgreso]', e);
      setError(e?.response?.data?.error || 'No se pudo guardar el progreso.');
    } finally {
      setGuardando(false);
    }
  }

  const subtareas = cita.subtareas || [];
  const subtotal = subtareas.length;
  const completadas_n = completadas.size;
  // Cerrar como realizada con alguna sin marcar exige motivo (el servidor lo vuelve a comprobar).
  const faltanSubtareas = estado === 'realizada' && subtotal > 0 && completadas_n < subtotal;
  const faltaMotivo = faltanSubtareas && !motivo.trim();

  // Fase 20b · #15 — 'no_asistio' solo tiene sentido en citas pasadas (el
  // backend lo rechaza con 400 fecha_futura). Espejamos la regla aquí: si la
  // cita no ha pasado, la opción "No asistió" no se muestra (default 'realizada').
  const esPasado = enPasado(cita);

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 z-50 grid place-items-center px-4 py-8 overflow-y-auto"
      onClick={guardando ? undefined : onClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg max-w-xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-100">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-900">
              Cerrar intervención
            </h3>
            <p className="text-xs text-slate-500 mt-0.5 truncate">
              {cita.campana_titulo} · {cita.usuario.nombre_completo}
            </p>
            <p className="text-xs text-slate-400">
              {cita.fecha} · {cita.hora_inicio}–{cita.hora_fin}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={guardando}
            className="w-7 h-7 grid place-items-center rounded-md hover:bg-slate-100 disabled:opacity-50"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {/* Cuerpo */}
        <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Estado */}
          <div>
            <p className="text-xs font-medium text-slate-700 mb-2">Resultado</p>
            <div className={esPasado ? 'grid grid-cols-2 gap-2' : 'grid grid-cols-1 gap-2'}>
              <BotonEstado
                activo={estado === 'realizada'}
                onClick={() => setEstado('realizada')}
                icon={<CheckCircle2 className="w-4 h-4" />}
                label="Realizada"
                tono="emerald"
              />
              {/* "No asistió" solo para citas pasadas (ver esPasado). */}
              {esPasado && (
                <BotonEstado
                  activo={estado === 'no_asistio'}
                  onClick={() => setEstado('no_asistio')}
                  icon={<XCircle className="w-4 h-4" />}
                  label="No asistió"
                  tono="amber"
                />
              )}
            </div>
          </div>

          {/* Fase 20b · #15 — consecuencias reales de marcar "No asistió". */}
          {estado === 'no_asistio' && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2.5 space-y-1">
              <p className="font-medium">Al confirmar:</p>
              <ul className="list-disc pl-5 space-y-0.5">
                <li>La participación del usuario volverá a <strong>pendiente</strong> — podrá reservar otra cita.</li>
                <li>Contará como una <strong>cancelación</strong> en su límite.</li>
                <li>Se le enviará una notificación por email + intranet.</li>
                <li>Esta acción <strong>no puede deshacerse</strong>.</li>
              </ul>
            </div>
          )}

          {/* Subtareas (si la campaña tiene) */}
          {subtotal > 0 && estado === 'realizada' && (
            <div>
              <p className="text-xs font-medium text-slate-700 mb-2">
                Subtareas{' '}
                <span className="text-slate-400 font-normal">
                  ({completadas_n}/{subtotal} hechas)
                </span>
              </p>
              <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                {subtareas.map((s) => (
                  <li key={s.id}>
                    <label className="flex items-start gap-2.5 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={completadas.has(s.id)}
                        onChange={() => toggleSubtarea(s.id)}
                        className="w-4 h-4 mt-0.5 rounded border-slate-300"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-slate-800">{s.nombre}</div>
                        {s.descripcion && (
                          <div className="text-xs text-slate-500 mt-0.5">
                            {s.descripcion}
                          </div>
                        )}
                      </div>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 3/11 · con alguna subtarea sin hacer, cerrar exige un motivo (texto plano). */}
          {faltanSubtareas && (
            <div>
              <p className="text-xs font-medium text-slate-700 mb-2">
                Motivo para cerrarla sin todas las subtareas <span className="text-red-600">*</span>
              </p>
              <textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                rows={2}
                maxLength={MOTIVO_MAX}
                placeholder='Ej. "El usuario no tenía el equipo; se hará en otra cita"'
                className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 resize-none"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Faltan {subtotal - completadas_n} de {subtotal}. Si vas a seguir otro día, usa «Guardar progreso».
              </p>
            </div>
          )}

          {/* Notas */}
          <div>
            <p className="text-xs font-medium text-slate-700 mb-2">
              Notas {estado === 'no_asistio' ? '(motivo)' : '(opcional)'}
            </p>
            <textarea
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              rows={3}
              placeholder={
                estado === 'no_asistio'
                  ? 'Ej. "Confirmó verbalmente que no venía"'
                  : 'Ej. "Instalación completada sin incidencias"'
              }
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 resize-none"
            />
          </div>

          {error && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md p-2.5 flex items-start gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-100 bg-slate-50 rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            disabled={guardando}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          {/* 3/11 · guarda las marcas sin cerrar la cita. */}
          {subtotal > 0 && estado === 'realizada' && (
            <button
              type="button"
              onClick={guardarProgreso}
              disabled={guardando}
              className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 bg-white text-slate-800 hover:bg-slate-50 disabled:opacity-50"
            >
              Guardar progreso
            </button>
          )}
          <button
            type="button"
            onClick={confirmar}
            disabled={guardando || faltaMotivo}
            title={faltaMotivo ? 'Escribe el motivo, o marca todas las subtareas' : undefined}
            className={
              'px-3 py-1.5 text-sm rounded-lg text-white flex items-center gap-1.5 disabled:opacity-60 ' +
              (estado === 'no_asistio'
                ? 'bg-amber-600 hover:bg-amber-700'
                : 'bg-emerald-600 hover:bg-emerald-700')
            }
          >
            {guardando && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {estado === 'realizada' ? 'Marcar como realizada' : 'Marcar como no asistió'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Fase 20b · #15 — ¿la cita ya ha pasado? (hora local del navegador; el backend
// valida con TZ Europe/Madrid — esto es defensa/UX, no la fuente de verdad).
function enPasado(cita) {
  if (!cita?.fecha || !cita?.hora_inicio) return false;
  const fechaISO = String(cita.fecha).slice(0, 10);
  const horaHHMM = String(cita.hora_inicio).slice(0, 5);
  const now = new Date();
  const hoyISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const ahoraHHMM = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  return fechaISO < hoyISO || (fechaISO === hoyISO && horaHHMM <= ahoraHHMM);
}

function BotonEstado({ activo, onClick, icon, label, tono }) {
  const claseActivo = tono === 'emerald'
    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
    : 'border-amber-500 bg-amber-50 text-amber-700';
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'border-2 rounded-lg py-3 px-4 flex items-center justify-center gap-2 text-sm font-medium transition-colors ' +
        (activo ? claseActivo : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400')
      }
    >
      {icon}
      {label}
    </button>
  );
}
